/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable */

import { useGetPublicProjects } from '@/adapters/projects';
import eosLogo from '@/assets/images/Logo_Main Color.svg';
import emptyPic from '@/assets/images/projects/empty.svg';
import DownloadButton from '@/components/common/DownloadButton';
import Loading from '@/components/Loading';
import { Words } from '@/entities/transcript';
import ProjectListItem from '@/types/project-list-item';
import { arrayToVTT } from '@/utils/transcript';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import { styled } from '@mui/material/styles';
import classNames from 'classnames';
import moment from 'moment';
import React, { FC, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import NoProjects from '../Projects/NoProjects';
import styles from '../Projects/Projects.module.scss';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  border: `1px solid var(--silver)`,
  borderRadius: '8px',
  marginBottom: '15px',
  '&:before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary {...props} />
))(() => ({
  backgroundColor: 'var(--gallery)',
  borderBottomColor: 'transparent',
  borderRadius: '8px',
  flexDirection: 'row-reverse',
  padding: 0,
  '&.Mui-expanded': {
    borderRadius: '8px 8px 0 0',
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    margin: 0,
    padding: '10px',
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(() => ({
  padding: 0,
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

/**
 * Public read-only view of a user's project library
 * Reason: Allow unauthenticated users to view shared projects without sidebar, upload, or edit capabilities
 */
const PublicProjects: FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { data: responseData, isLoading } = useGetPublicProjects(userId || '');

  // Extract projects and user data from response
  const allProjects = responseData?.body?.projects || [];
  const userData = responseData?.body?.user;
  const [transcriptMenuAnchor, setTranscriptMenuAnchor] = useState<{
    [key: string]: HTMLElement | null;
  }>({});

  // we only want projects with edits that have overlay history
  const projects = useMemo(
    () =>
      allProjects
        .map((project: ProjectListItem) => ({
          ...project,
          edits: project.edits.filter(
            (edit) => edit.overlayHistory && edit.overlayHistory.length > 0,
          ),
        }))
        .filter((project: ProjectListItem) => project.edits.length > 0),
    [allProjects],
  );

  /**
   * Handles opening the transcript export menu
   * Reason: Allow users to export transcripts in different formats
   */
  const handleTranscriptMenuOpen = (editId: string, event: React.MouseEvent<HTMLElement>): void => {
    setTranscriptMenuAnchor({ ...transcriptMenuAnchor, [editId]: event.currentTarget });
  };

  /**
   * Handles closing the transcript export menu
   * Reason: Clean up menu state when user closes or selects an option
   */
  const handleTranscriptMenuClose = (editId: string): void => {
    setTranscriptMenuAnchor({ ...transcriptMenuAnchor, [editId]: null });
  };

  /**
   * Downloads transcript in specified format
   * Reason: Provide transcript export functionality similar to authenticated view
   */
  const downloadTranscript = (transcriptUrl: string, fileName: string, format: string): void => {
    fetch(transcriptUrl)
      .then((response) => response.json())
      .then((data) => {
        // Extract words from Deepgram transcript format
        const words = data.results?.channels?.[0]?.alternatives?.[0]?.words || [];

        let blob: Blob;
        if (format === 'txt') {
          // Convert JSON transcript to plain text
          const content = words.map((word: any) => word.punctuated_word || word.word).join(' ');
          blob = new Blob([content], { type: 'text/plain' });
        } else if (format === 'vtt') {
          // Use existing arrayToVTT utility from transcript.ts
          blob = arrayToVTT(words as Words[]);
        } else {
          throw new Error(`Unsupported format: ${format}`);
        }

        // Create and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${fileName}.${format}`;
        link.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error('Failed to download transcript:', error);
        alert('Failed to download transcript. Please try again.');
      });
  };

  return (
    <>
      <Box
        sx={{
          padding: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <img src={eosLogo} alt="Edit on the Spot" style={{ height: '40px' }} />
        {userData && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            {userData.brandKitLogo && (
              <img
                src={userData.brandKitLogo}
                alt={userData.name}
                style={{ height: '40px', maxWidth: '200px', objectFit: 'contain' }}
              />
            )}
          </Box>
        )}
      </Box>
      <Box sx={{ padding: '0 24px' }}>
        <div className={styles.navbar}>
          <h1 className={styles.projectsHeadTitle}>Public Projects Library</h1>
        </div>
        {isLoading ? (
          <Loading />
        ) : (projects as any[]).length === 0 ? (
          <NoProjects
            media={<img src={emptyPic} alt="no projects" />}
            text={
              <>
                <p>No projects available.</p>
              </>
            }
          />
        ) : (
          <>
            <div className={styles.table}>
            <div className={styles.tableTitle}>
              <div className={styles.tableItem}>Project Name</div>
              <div className={styles.tableItem}>Date</div>
              <div className={classNames(styles.tableItem, styles.hiddenSm)}>Time</div>
              <div className={styles.tableItem}>Action</div>
            </div>
            {projects.map((project) => (
              <Accordion key={project.id} defaultExpanded={false}>
                <AccordionSummary>
                  <Typography
                    sx={{ flex: '5 1', padding: 0 }}
                    className={styles.tableItem + ' ' + styles.specialColumn}
                  >
                    {project.name}
                  </Typography>
                  <Typography sx={{ flex: '1 1', padding: 0 }} className={styles.specialColumn}>
                    {moment(project.created).format('MMMM D, YYYY')}
                  </Typography>
                  <Typography sx={{ flex: '1 1', padding: 0 }} className={styles.specialColumn}>
                    {moment(project.created).format('hh:mm A')}
                  </Typography>
                  <Typography sx={{ flex: '1 1', padding: 0 }}></Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {(project.edits as any[]).length === 0 && (
                    <Box sx={{ display: 'flex', padding: '10px' }}>
                      <Typography sx={{ flex: '5 1', padding: 0 }}>No edits found.</Typography>
                    </Box>
                  )}
                  {project.edits.map((edit) => (
                    <React.Fragment key={edit.id}>
                      {(edit.status === 'Processed' || edit.status === 'Processing') && (
                        <Box sx={{ padding: '10px', borderBottom: '1px solid #f0f0f0' }}>
                          {/* Edit header row */}
                          <Box sx={{ display: 'flex', marginBottom: '10px' }}>
                            <Typography
                              sx={{
                                flex: '5 1',
                                padding: 0,
                                lineHeight: '32px',
                                fontWeight: 'bold',
                              }}
                              className={styles.tableItem + ' ' + styles.specialColumn}
                            >
                              {edit.name}
                            </Typography>
                            <Typography
                              sx={{ flex: '1 1', padding: 0, lineHeight: '32px' }}
                              className={styles.specialColumn}
                            >
                              {moment(project.created).format('MMMM D, YYYY')}
                            </Typography>
                            <Typography
                              sx={{ flex: '1 1', padding: 0, lineHeight: '32px' }}
                              className={styles.specialColumn}
                            >
                              {moment(project.created).format('hh:mm A')}
                            </Typography>
                            <Box
                              sx={{ flex: '1 1', padding: 0, display: 'flex', gap: '5px' }}
                              className={styles.tableBtn}
                            >
                              {edit.transcript && (
                                <>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={(e) => handleTranscriptMenuOpen(edit.id, e)}
                                    endIcon={<KeyboardArrowDownIcon />}
                                    sx={{ paddingLeft: 1, paddingRight: 1, maxHeight: '32px' }}
                                  >
                                    Export
                                  </Button>
                                  <Menu
                                    anchorEl={transcriptMenuAnchor[edit.id]}
                                    open={Boolean(transcriptMenuAnchor[edit.id])}
                                    onClose={() => handleTranscriptMenuClose(edit.id)}
                                  >
                                    <MenuItem
                                      onClick={() => {
                                        if (edit.transcript) {
                                          downloadTranscript(edit.transcript, edit.name, 'txt');
                                        }
                                        handleTranscriptMenuClose(edit.id);
                                      }}
                                    >
                                      TXT
                                    </MenuItem>
                                    <MenuItem
                                      onClick={() => {
                                        if (edit.transcript) {
                                          downloadTranscript(edit.transcript, edit.name, 'vtt');
                                        }
                                        handleTranscriptMenuClose(edit.id);
                                      }}
                                    >
                                      VTT
                                    </MenuItem>
                                  </Menu>
                                </>
                              )}
                            </Box>
                          </Box>

                          {/* Overlay history section */}
                          {edit.overlayHistory && edit.overlayHistory.length > 0 && (
                            <Box sx={{ marginLeft: '20px' }}>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 'bold', marginBottom: '8px' }}
                              >
                                Overlay History:
                              </Typography>
                              <List dense sx={{ padding: 0 }}>
                                {edit.overlayHistory
                                  .slice()
                                  .reverse()
                                  .map((overlay: any, index: number) => (
                                    <ListItem
                                      key={overlay.timestamp}
                                      sx={{
                                        bgcolor: 'white',
                                        mb: 1,
                                        borderRadius: 1,
                                        border: '1px solid #e0e0e0',
                                        padding: '8px 12px',
                                      }}
                                    >
                                      <ListItemText
                                        primary={`${index === 0 ? '(Latest) ' : ''}${
                                          overlay.format.charAt(0).toUpperCase() +
                                          overlay.format.slice(1)
                                        }`}
                                        secondary={new Date(overlay.timestamp).toLocaleString()}
                                        sx={{ flex: '1 1', margin: 0 }}
                                      />
                                      <Box sx={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
                                        <DownloadButton
                                          url={overlay.overlayPath}
                                          fileName={`${edit.name}.mp4`}
                                          size="small"
                                          variant="contained"
                                        />
                                      </Box>
                                    </ListItem>
                                  ))}
                              </List>
                            </Box>
                          )}
                        </Box>
                      )}
                    </React.Fragment>
                  ))}
                </AccordionDetails>
              </Accordion>
            ))}
            <div className={styles.tableTitle}>
              <div className={styles.tableItem}>Project Name</div>
              <div className={styles.tableItem}>Date</div>
              <div className={classNames(styles.tableItem, styles.hiddenSm)}>Time</div>
              <div className={styles.tableItem}>Action</div>
            </div>
          </div>
        </>
      )}
      </Box>
    </>
  );
};

export default PublicProjects;
